import type { ConstValues } from '@openx/types/helpers.ts';
import type { Option } from '@openx/types/options.ts';
import type React from 'react';
export type BaseConfigurableFilter<K> = {
  name: string;
  subName?: string;
  /** The key used to identify the filter in the criteria and to set URL params correctly */
  key: K;
  highlighted?: boolean;
  applyImmediatly?: boolean;
  withLoader?: boolean;
  /**
   * When false, the filter cannot be cleared: the single-filter clear (x) icon is hidden
   * and the value is preserved by "Clear filters". Defaults to true.
   */
  clearable?: boolean;
};

export type FormatAndDevicesConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.FORMAT_AND_DEVICES;
  options?: never;
  config: {
    managedKeys: [string, ...string[]];

    renderContent: (props: {
      appliedFilters?: FilterCriteria;
      onOptionChange: (key: string, options: FilterValue) => void;
      isDisabled?: boolean;
      onInnerPopoverOpen: (open: boolean) => void;
    }) => React.ReactNode;

    getButtonValue?: (filters?: FilterCriteria) => string;
  };
};

export type ConfigurableFilter<K> =
  | CheckboxConfigurableFilter<K>
  | DropdownConfigurableFilter<K>
  | RadioConfigurableFilter<K>
  | AutocompleteConfigurableFilter<K>
  | RangeConfigurableFilter<K>
  | MultiOptionWithRangeConfigurableFilter<K>
  | MultiOptionWithRadioConfigurableFilter<K>
  | GreaterLessFilter<K>
  | NumericInputConfigurableFilter<K>
  | DateRangeConfigurableFilter<K>
  | SwitchConfigurableFilter<K>
  | TextInputConfigurableFilter<K>
  | HierarchicalCheckboxConfigurableFilter<K>
  | MultiSelectWithRadioConfigurableFilter<K>
  | RangeWithRadioConfigurableFilter<K>
  | FormatAndDevicesConfigurableFilter<K>
  | SearchAndSelectConfigurableFilter<K>;

export const FILTER_TYPES = {
  CHECKBOX: 'checkbox',
  DROPDOWN: 'dropdown',
  RADIO: 'radio',
  AUTOCOMPLETE: 'autocomplete',
  RANGE: 'range',
  MULTI_OPTION_WITH_RANGE: 'multi-option-with-range',
  MULTI_OPTION_WITH_RADIO: 'multi-option-with-radio',
  GREATER_LESS: 'greater-less',
  NUMERIC_INPUT: 'numeric-input',
  DATE_RANGE: 'date-range',
  SWITCH: 'switch',
  TEXT_INPUT: 'text-input',
  HIERARCHICAL_CHECKBOX: 'hierarchical-checkbox',
  MULTI_SELECT_WITH_RADIO: 'multi-select-with-radio',
  RANGE_WITH_RADIO: 'range-with-radio',
  FORMAT_AND_DEVICES: 'FORMAT_AND_DEVICES',
  SEARCH_AND_SELECT: 'search-and-select',
} as const;

export type FilterType = ConstValues<typeof FILTER_TYPES>;

export type CheckboxConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.CHECKBOX;
  /** At least one option is required */
  options: [Option, ...Option[]];
};

export type DropdownConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.DROPDOWN;
  /** At least one option is required */
  options: [Option, ...Option[]];
  config?: {
    emptyPlaceholder?: string;
    defaultValueLabel?: string;
    dropdownHeight?: string;
  };
};

export type HierarchicalCheckboxConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.HIERARCHICAL_CHECKBOX;
  /** At least one option is required */
  options: [Option, ...Option[]];
};

export type RadioConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.RADIO;
  /** At least one option is required */
  options: [Option, ...Option[]];
  orientation?: 'row' | 'column';
  defaultOption?: string;
};

export type GreaterLessFilter<K> = BaseConfigurableFilter<K> & {
  type: 'greater-less';
  /** At least one option is required */
  options: [Option, ...Option[]];
  config?: {
    criteriaValueSuffix?: string;
    emptyPlaceHolder?: string;
    valueChangeValidator?: (currentValue: string) => boolean;
  };
};

export const GREATER_LESS_FILTER_OPTIONS = {
  Greater: 'gt',
  Less: 'lt',
} as const;

export type GreaterLessOptionType = ConstValues<typeof GREATER_LESS_FILTER_OPTIONS>;

export type AutocompleteConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.AUTOCOMPLETE;
  /** At least one option is required */
  options: Option[];
  config?: {
    dropdownHeight?: string;
    emptyPlaceholder?: string;
    isLoading?: boolean;
    selectedPlaceholder?: string;
  };
};

export type RangeConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.RANGE;
  options?: never;
  config?: {
    inputType: 'integer' | 'float';
    precision?: number;
    startAdornment?: string;
    endAdornment?: string;
    debounce?: number;
    fieldPlaceholder?: string;
    detailedOptionsConfig?: {
      minFieldPlaceholder?: string;
      maxFieldPlaceholder?: string;
      minValue?: number;
      maxValue?: number;
    };
  };
};

export type DateRangeConfigurableFilter<K = string> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.DATE_RANGE;
  options?: never;
  config?: {
    inputFormat?: string;
    inputPlaceholder?: string;
    maxDate?: Date;
    minDate?: Date;
  };
};

export type MultiOptionWithRangeConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.MULTI_OPTION_WITH_RANGE;
  options: Option[];
  config: {
    inputType: 'integer' | 'float';
    precision?: number;
    debounce?: number;
    startAdornment?: string;
    endAdornment?: string;
    fieldPlaceholder?: string;
    detailedOptionsConfig?: {
      minFieldPlaceholder?: string;
      maxFieldPlaceholder?: string;
      multiOptionsConfig?: {
        endAdornment?: string;
        minValue?: number;
        maxValue?: number;
      }[];
    };
  };
};

export type RangeWithRadioConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.RANGE_WITH_RADIO;
  options: Option[];
  config: {
    removedAvailableOptions?: { optionId: string; removedOptionId: string }[];
    inputType: 'integer' | 'float';
    precision?: number;
    availableOptions?: Option[];
    rangeFilterKey: string;
    radioFilterKey: string;
    startAdornment?: string;
    endAdornment?: string;
    rangeLabel?: string;
    radioLabel?: string;
    defaultOption?: string;
    detailedOptionsConfig?: {
      minFieldPlaceholder?: string;
      maxFieldPlaceholder?: string;
      minValue?: number;
      maxValue?: number;
      multiOptionsConfig?: {
        endAdornment?: string;
      }[];
    };
  };
};

export type MultiOptionWithRadioConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.MULTI_OPTION_WITH_RADIO;
  options: [Option, ...Option[]];
  config: {
    availableOptions: Option[];
    optionsWithRadio?: string[];
    radioOptionsLabel?: string;
    removedAvailableOptions?: { optionId: string; removedOptionId: string }[];
  };
};

export type MultiSelectWithRadioOptionType = 'select' | 'tree';
export type MultiSelectWithRadioConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.MULTI_SELECT_WITH_RADIO;
  options: [Option, ...Option[]];
  config: {
    availableOptions: Record<string, { type: MultiSelectWithRadioOptionType; options: Option[] }>;
    removedAvailableOptions?: { optionId: string; removedOptionId: string }[];
  };
};

export type FilterValue =
  | string
  | string[]
  | [string | undefined, string | undefined]
  | number
  | boolean
  | null
  | undefined;
export type FilterCriteria = Record<string, FilterValue>;
export type RangeSelectorValue = { min?: string; max?: string };

export type NumericInputConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.NUMERIC_INPUT;
  options?: never;
  config?: {
    inputType: 'integer' | 'float';
    precision?: number;
    startAdornment?: string;
    endAdornment?: string;
    fieldPlaceholder?: string;
    customRegex?: RegExp;
    label?: string;
  };
};

export type SwitchConfigurableFilter<K = string> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.SWITCH;
  options: [Option];
};

export type TextInputConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  type: typeof FILTER_TYPES.TEXT_INPUT;
  options?: never;
  config?: {
    startAdornment?: string;
    endAdornment?: string;
    fieldPlaceholder?: string;
    customRegex?: RegExp;
    debounce?: number;
  };
};

export type SearchAndSelectConfigurableFilter<K> = BaseConfigurableFilter<K> & {
  isLoading?: boolean;
  onInputChange: (optionId: string) => void;
  noOptionsText?: string;
  options: Option[];
  type: typeof FILTER_TYPES.SEARCH_AND_SELECT;
};
